/** Original low-poly forest forms. Seeded geometry, one material, no textures. */
import * as THREE from "three";
import { range, type Rng } from "@hyperlight/shared";
import { paint, shade, V } from "./primitives";
import type { C3 } from "./types";
export { buildBoulder } from './rockForms';

/** Closed bough shell: a scalloped skirt, broad shoulder and off-centre tip.
 * Shared rings avoid cracks; alternating tips break the Christmas-cone outline. */
export function bough(rng: Rng, y: number, radius: number, height: number, color: C3): THREE.BufferGeometry {
  const sides = 10, phase = rng() * Math.PI * 2;
  const rings: THREE.Vector3[][] = [];
  for (let j = 0; j < 3; j++) {
    const ring: THREE.Vector3[] = [];
    for (let i = 0; i < sides; i++) {
      const a = phase + i / sides * Math.PI * 2;
      const r = radius * [1, .74, .24][j] * range(rng, .88, 1.12);
      ring.push(V(Math.cos(a) * r, y + height * [i % 2 ? .12 : 0, .28, .76][j], Math.sin(a) * r));
    }
    rings.push(ring);
  }
  const tip = V(radius * .12, y + height, -radius * .08), base = V(0, y + height * .08, 0);
  const positions: number[] = [];
  const tri = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3) => positions.push(...a.toArray(), ...b.toArray(), ...c.toArray());
  for (let i = 0; i < sides; i++) {
    const next = (i + 1) % sides;
    tri(base, rings[0][i], rings[0][next]);
    for (let j = 0; j < 2; j++) {
      tri(rings[j][i], rings[j + 1][i], rings[j][next]);
      tri(rings[j][next], rings[j + 1][i], rings[j + 1][next]);
    }
    tri(rings[2][i], tip, rings[2][next]);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  paint(geo, color, .7);
  geo.computeVertexNormals();
  const colors = geo.getAttribute("color"), normals = geo.getAttribute("normal");
  // Subtle material separation on undersides; dynamic lighting still supplies shadows.
  for (let i = 0; i < colors.count; i++) {
    const k = .84 + .16 * Math.max(0, normals.getY(i));
    colors.setXYZ(i, color[0] * k, color[1] * k, color[2] * k);
  }
  return geo;
}

/** Seven arched fronds, each with paired pointed leaflets. Closed leaflets
 * read from above and below without needing a separate double-sided material. */
export function buildFern(rng: Rng, leaf: C3): THREE.BufferGeometry {
  const positions: number[] = [], colors: number[] = [], sways: number[] = [];
  for (let f = 0; f < 7; f++) {
    const a = f / 7 * Math.PI * 2 + rng() * .3;
    const length = range(rng, .55, .95), rise = range(rng, .35, .65);
    const along = V(Math.cos(a), 0, Math.sin(a)), across = V(-Math.sin(a), 0, Math.cos(a));
    const at = (t: number) => along.clone().multiplyScalar(t * length).add(V(0, .06 + Math.sin(t * Math.PI * .8) * rise, 0));
    for (let i = 0; i < 5; i++) {
      const t = .14 + i * .17;
      for (const side of [-1, 1]) {
        const root = at(t), end = at(t + .18).addScaledVector(across, side * .23 * (1 - t));
        const ridge = root.clone().lerp(end, .45).add(V(0, .035, 0));
        const back = at(t + .12);
        const c = shade(leaf, .85 + t * .25);
        for (const p of [root, ridge, end, end, ridge, back, root, end, back, root, back, ridge]) {
          positions.push(...p.toArray()); colors.push(...c); sways.push(t);
        }
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geo.setAttribute("sway", new THREE.Float32BufferAttribute(sways, 1));
  geo.computeVertexNormals();
  return geo;
}
